import type { ModuleGridType, StudioModuleType } from 'types/studio/items'

import {
  applySharedVanDerValkAzimuthHorizontalOffset,
  buildVanDerValkFieldLocationFromGrid,
  sharedVanDerValkAzimuthHorizontalOffsetFromLocation,
  snapVanDerValkLocationAzimuthToRoofDownslope,
  type VanDerValkBuildFieldLocationErrorReason,
  type VanDerValkFieldLocation,
} from './buildVanDerValkFieldLocation'
import {
  buildVanDerValkPitchedRoofSlopeFromModuleGrid,
  computeVanDerValkRoofDownslopeHorizontalUnit,
  type VanDerValkBuildPitchedRoofSlopeErrorReason,
} from './buildVanDerValkPitchedRoofSlope'
import type { VanDerValkBomFrameFromSuitability } from './fetchVanDerValkMountingBom'
import { deriveVanDerValkPitchedInclinationOverrideFromModuleGrid } from './vanDerValkPitchedInclinationOverride'
import { getVanDerValkModuleGridsForBom } from './vanDerValkModuleGrids'
import { normalizeVanDerValkGridCells, type VanDerValkNormalizedGridCell } from './vanDerValkNormalizeGridCells'

export { normalizeVanDerValkGridCells } from './vanDerValkNormalizeGridCells'
export type { VanDerValkNormalizedGridCell } from './vanDerValkNormalizeGridCells'

export type VanDerValkFrameCellCode = 0 | 1 | 2 | 3

export type VanDerValkBuildFramesErrorReason =
  | 'no_active_layout'
  | 'matrix_too_large'
  | 'ew_requires_dual_tilt'
  | VanDerValkBuildFieldLocationErrorReason

export type VanDerValkBomFieldInput = {
  frames: VanDerValkFrameCellCode[][]
  fieldLabel?: string
  location: VanDerValkFieldLocation
}

export type VanDerValkPitchedBomFieldInput = VanDerValkBomFieldInput & {
  inclinationOverride: number
}

export type BuildVanDerValkFramesForGridResult =
  | { ok: true; frames: VanDerValkFrameCellCode[][] }
  | { ok: false; reason: VanDerValkBuildFramesErrorReason }

export type BuildVanDerValkFieldsFromSystemResult =
  | { ok: true; fields: VanDerValkBomFieldInput[] }
  | { ok: false; reason: VanDerValkBuildFramesErrorReason }

export type BuildVanDerValkPitchedFieldsFromSystemResult =
  | { ok: true; fields: VanDerValkPitchedBomFieldInput[] }
  | { ok: false; reason: VanDerValkBuildFramesErrorReason | VanDerValkBuildPitchedRoofSlopeErrorReason }

const MAX_MATRIX_DIM = 50

const GEO_EAST_AZIMUTH = 90
const GEO_WEST_AZIMUTH = 270

export type { VanDerValkBuildFieldLocationErrorReason } from './buildVanDerValkFieldLocation'

type NormalizedCell = VanDerValkNormalizedGridCell

function angularDistanceDeg(a: number, b: number): number {
  const diff = Math.abs(a - b) % 360
  return diff > 180 ? 360 - diff : diff
}

function azimuthToHalfPeakCode(azimuth: number): 2 | 3 {
  const a = ((azimuth % 360) + 360) % 360
  const distEast = angularDistanceDeg(a, GEO_EAST_AZIMUTH)
  const distWest = angularDistanceDeg(a, GEO_WEST_AZIMUTH)
  return distEast <= distWest ? 2 : 3
}

function normalizeCellsActive(cellsActive: string[]): NormalizedCell[] | null {
  return normalizeVanDerValkGridCells(cellsActive)
}

function cellLookupKey(ncol: number, nrow: number): string {
  return `${ncol},${nrow}`
}

function buildModuleAzimuthByCellKey(grid: ModuleGridType): Map<string, number> {
  const map = new Map<string, number>()
  const modules = grid.getModules?.() ?? []
  for (const mod of modules as StudioModuleType[]) {
    const cell = mod.cell
    if (typeof cell !== 'string') {
      continue
    }
    const getAzimuth = (mod as StudioModuleType & { getAzimuth?: () => number }).getAzimuth
    if (typeof getAzimuth === 'function') {
      map.set(cell, getAzimuth.call(mod))
    }
  }
  return map
}

function isGridDualTiltEwReady(grid: ModuleGridType): boolean {
  return grid.getPanelConfiguration() === 'DUAL_TILT_RACK' && grid.modulesPerRow() === 2
}

function allocateMatrix(numRows: number, numCols: number): VanDerValkFrameCellCode[][] | null {
  if (numRows < 1 || numCols < 1 || numRows > MAX_MATRIX_DIM || numCols > MAX_MATRIX_DIM) {
    return null
  }
  return Array.from({ length: numRows }, () => Array.from({ length: numCols }, () => 0 as VanDerValkFrameCellCode))
}

function buildNsFrames(normalized: NormalizedCell[]): BuildVanDerValkFramesForGridResult {
  const maxCol = Math.max(...normalized.map((c) => c.ncol))
  const maxRow = Math.max(...normalized.map((c) => c.nrow))
  const matrix = allocateMatrix(maxRow + 1, maxCol + 1)
  if (!matrix) {
    return { ok: false, reason: 'matrix_too_large' }
  }
  const active = new Set(normalized.map((c) => cellLookupKey(c.ncol, c.nrow)))
  for (let row = 0; row <= maxRow; row++) {
    for (let col = 0; col <= maxCol; col++) {
      if (active.has(cellLookupKey(col, row))) {
        matrix[row][col] = 1
      }
    }
  }
  return { ok: true, frames: matrix }
}

function buildEwFrames(grid: ModuleGridType, normalized: NormalizedCell[]): BuildVanDerValkFramesForGridResult {
  if (!isGridDualTiltEwReady(grid)) {
    return { ok: false, reason: 'ew_requires_dual_tilt' }
  }
  const modulesPerRow = grid.modulesPerRow()
  const maxCol = Math.max(...normalized.map((c) => c.ncol))
  const maxRow = Math.max(...normalized.map((c) => c.nrow))
  const numPairRows = Math.floor((maxRow + modulesPerRow) / modulesPerRow)
  const matrix = allocateMatrix(numPairRows, maxCol + 1)
  if (!matrix) {
    return { ok: false, reason: 'matrix_too_large' }
  }
  const active = new Set(normalized.map((c) => cellLookupKey(c.ncol, c.nrow)))
  const azimuthByCell = buildModuleAzimuthByCellKey(grid)

  for (let col = 0; col <= maxCol; col++) {
    for (let pairRow = 0; pairRow < numPairRows; pairRow++) {
      const rowA = pairRow * modulesPerRow
      const rowB = rowA + 1
      const keyA = cellLookupKey(col, rowA)
      const keyB = rowB <= maxRow ? cellLookupKey(col, rowB) : null
      const activeA = active.has(keyA)
      const activeB = keyB != null && active.has(keyB)

      if (activeA && activeB) {
        matrix[pairRow][col] = 1
      } else if (!activeA && !activeB) {
        matrix[pairRow][col] = 0
      } else {
        const physicalKey = activeA
          ? normalized.find((c) => c.ncol === col && c.nrow === rowA)?.cellKey
          : normalized.find((c) => keyB != null && c.ncol === col && c.nrow === rowB)?.cellKey
        const azimuth = physicalKey != null ? azimuthByCell.get(physicalKey) : undefined
        if (azimuth === undefined || !Number.isFinite(azimuth)) {
          matrix[pairRow][col] = 2
        } else {
          matrix[pairRow][col] = azimuthToHalfPeakCode(azimuth)
        }
      }
    }
  }
  return { ok: true, frames: matrix }
}

function buildSolarRampFrames(): BuildVanDerValkFramesForGridResult {
  // Vendor Solar Ramp contract: always [[1]] per field; not a 1:1 map to panel count.
  return { ok: true, frames: [[1]] }
}

export function buildVanDerValkFramesForModuleGrid(
  grid: ModuleGridType,
  panelType: VanDerValkBomFrameFromSuitability['panelType'],
  options?: { solarRamp?: boolean }
): BuildVanDerValkFramesForGridResult {
  const cellsActive = grid.cellsActive
  if (!Array.isArray(cellsActive) || cellsActive.length === 0) {
    return { ok: false, reason: 'no_active_layout' }
  }
  const normalized = normalizeCellsActive(cellsActive)
  if (!normalized?.length) {
    return { ok: false, reason: 'no_active_layout' }
  }

  if (options?.solarRamp) {
    return buildSolarRampFrames()
  }

  if (panelType === 'EW') {
    return buildEwFrames(grid, normalized)
  }
  return buildNsFrames(normalized)
}

export function buildVanDerValkFieldsFromSystem(
  system: Parameters<typeof getVanDerValkModuleGridsForBom>[0],
  bomFrame: VanDerValkBomFrameFromSuitability,
  options?: { solarRamp?: boolean }
): BuildVanDerValkFieldsFromSystemResult {
  const grids = getVanDerValkModuleGridsForBom(system)
  if (!grids.length) {
    return { ok: false, reason: 'no_active_layout' }
  }

  const fields: VanDerValkBomFieldInput[] = []
  for (const grid of grids) {
    const built = buildVanDerValkFramesForModuleGrid(grid, bomFrame.panelType, options)
    if (!built.ok) {
      return { ok: false, reason: built.reason }
    }
    const locationBuilt = buildVanDerValkFieldLocationFromGrid(grid, { rowPitchMm: bomFrame.rowPitch })
    if (!locationBuilt.ok) {
      return { ok: false, reason: locationBuilt.reason }
    }
    fields.push({
      frames: built.frames,
      fieldLabel: grid.uuid ? String(grid.uuid) : '',
      location: locationBuilt.location,
    })
  }
  return { ok: true, fields }
}

export function buildVanDerValkPitchedFieldsFromSystem(
  system: Parameters<typeof getVanDerValkModuleGridsForBom>[0]
): BuildVanDerValkPitchedFieldsFromSystemResult {
  return buildVanDerValkPitchedFieldsFromGrids(getVanDerValkModuleGridsForBom(system))
}

export function buildVanDerValkPitchedFieldsFromGrids(
  grids: ModuleGridType[]
): BuildVanDerValkPitchedFieldsFromSystemResult {
  if (!grids.length) {
    return { ok: false, reason: 'no_active_layout' }
  }

  const roofSlopeBuilt = buildVanDerValkPitchedRoofSlopeFromModuleGrid(grids[0])
  const roofPolygon = roofSlopeBuilt.ok ? roofSlopeBuilt.roofSlope.roofPolygon : undefined

  const fields: VanDerValkPitchedBomFieldInput[] = []
  for (const grid of grids) {
    const inclinationBuilt = deriveVanDerValkPitchedInclinationOverrideFromModuleGrid(grid)
    if (!inclinationBuilt.ok) {
      return { ok: false, reason: inclinationBuilt.reason }
    }
    const built = buildVanDerValkFramesForModuleGrid(grid, 'NS')
    if (!built.ok) {
      return { ok: false, reason: built.reason }
    }
    const locationBuilt = buildVanDerValkFieldLocationFromGrid(grid, { roofPolygon })
    if (!locationBuilt.ok) {
      return { ok: false, reason: locationBuilt.reason }
    }
    fields.push({
      frames: built.frames,
      fieldLabel: grid.uuid ? String(grid.uuid) : '',
      location: locationBuilt.location,
      inclinationOverride: inclinationBuilt.inclinationOverride,
    })
  }

  const downslopeUnit = roofPolygon ? computeVanDerValkRoofDownslopeHorizontalUnit(roofPolygon) : null
  if (downslopeUnit && fields.length > 0) {
    const refSnappedAzimuth = snapVanDerValkLocationAzimuthToRoofDownslope(
      fields[0].location.base,
      fields[0].location.azimuth,
      downslopeUnit
    )
    const sharedOffset = sharedVanDerValkAzimuthHorizontalOffsetFromLocation(
      fields[0].location.base,
      refSnappedAzimuth
    )
    if (sharedOffset) {
      for (const field of fields) {
        field.location = {
          ...field.location,
          azimuth: applySharedVanDerValkAzimuthHorizontalOffset(
            field.location.base,
            field.location.azimuth,
            sharedOffset
          ),
        }
      }
    }
  }

  return { ok: true, fields }
}

export function vanDerValkFramesErrorMessage(reason: VanDerValkBuildFramesErrorReason): string {
  switch (reason) {
    case 'no_active_layout':
      return 'Place at least one panel before generating the Van der Valk BOM.'
    case 'matrix_too_large':
      return 'Panel layout is too large for the Van der Valk API.'
    case 'ew_requires_dual_tilt':
      return 'East–West suitability requires Dual-tilt racking on every panel group.'
    case 'invalid_field_location':
      return 'Cannot determine panel field position for the Van der Valk BOM. Check the panel layout and try again.'
    default:
      return 'Cannot build Van der Valk panel layout.'
  }
}
